import React from 'react'
import PropTypes from 'prop-types'
import {
  Container,
  Text,
  Price,
  Level,
  Clipped,
  Divider,
  Button,
  Sticky,
  Drawer,
  Media,
  Box,
  Image,
  Disclosure,
} from '@deseretbook/react-ui'
import { usePopup } from '@deseretbook/react-utils'
import { withCheckout } from './CheckoutContext'
import PlatinumPointsDisplay from '../PlatinumPointsValue'

/**
 * Mobile version of the checkout page's order summary.
 *
 * @returns {function} component
 */
const MiniOrderSummary = ({ step, order, submitOrder, isLoading, isSubmitting }) => {
  const popup = usePopup()

  const appliedGiftCardBalance = order.payments.filter(({ state }) => state === 'checkout')
    .filter(({ sourceType }) => sourceType === 'DeseretBook::GiftCard')
    .reduce((acc, obj) => acc + parseFloat(obj.amount), 0)

  let orderTotal = order.displayTotal

  if (appliedGiftCardBalance > 0) {
    orderTotal = parseFloat(order.displayTotal) - appliedGiftCardBalance
  }

  return (
    <Sticky position="bottom" background="alternate">
      <Box paddingVertical={0.5} flex={0}>
        <Container>
          <Level>
            <Level.Left isShrinkable>
              <Level.Item>
                <Clipped>
                  <Text hasEllipsis size="tiny">
                    {
                      order.state === 'confirm'
                        ? 'Total'
                        : 'Estimated Total'
                    }
                  </Text>
                  <Text hasEllipsis isBold><Price value={orderTotal} /></Text>
                </Clipped>
              </Level.Item>
              <Level.Item>
                <Button
                  type="button"
                  size="small"
                  {...popup.triggerProps}
                  disabled={isLoading}
                >
                  Order Details
                </Button>
              </Level.Item>
            </Level.Left>
            {isSubmitting || (!isLoading && step === 'confirm')
              ? (
                <Level.Right>
                  <Button
                    type="button"
                    icon="lock"
                    color="action"
                    isFullwidth
                    isLoading={isSubmitting}
                    onClick={submitOrder}
                  >
                    Place Order
                  </Button>
                </Level.Right>
              )
              : null
            }
          </Level>
        </Container>
      </Box>

      <Drawer {...popup.popupProps}>
        <Drawer.Header>
          <Text isBold hasEllipsis>Order Details</Text>
        </Drawer.Header>
        <Drawer.Content>
          <Box paddingBottom={0.5}>
            <Level>
              <Level.Left isShrinkable>
                <Level.Item>
                  <Text hasEllipsis>Subtotal</Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Text hasEllipsis><Price value={order.displayItemTotal} /></Text>
                </Level.Item>
              </Level.Right>
            </Level>
          </Box>
          <Box paddingBottom={0.5}>
            <Level>
              <Level.Left isShrinkable>
                <Level.Item>
                  <Clipped>
                    <Text hasEllipsis>Shipping</Text>
                    {
                      order.shipments.map(({
                        shippingRates,
                        shippingMethods,
                        id: shipmentId,
                        retailStore,
                      }) => {
                        const selectedShippingRate = shippingRates
                          .filter(({ selected }) => selected)[0]
                        const selectedShippingMethod = selectedShippingRate && shippingMethods
                          .filter(({ id }) => selectedShippingRate.shippingMethodId === id)[0]

                        if (!selectedShippingMethod) {
                          return null
                        }

                        const isStorePickup = retailStore && selectedShippingMethod.adminName === 'Store Pickup'

                        return (
                          <Text size="tiny" key={shipmentId}>
                            {
                              (isStorePickup && retailStore && retailStore.displayName)
                                ? `${selectedShippingMethod.name} (${retailStore.displayName} ${retailStore.shippingTime})`
                                : selectedShippingMethod.name
                            }
                          </Text>
                        )
                      })
                    }
                  </Clipped>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Text hasEllipsis><Price value={order.displayShipTotal} /></Text>
                </Level.Item>
              </Level.Right>
            </Level>
          </Box>

          {
            order.platinumPointsTotal < 0
              ? (
                <Box paddingBottom={0.5}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Level.Item>
                        <Text hasEllipsis>Applied Platinum Points</Text>
                      </Level.Item>
                    </Level.Left>
                    <Level.Right>
                      <Level.Item>
                        <Text hasEllipsis>
                          <PlatinumPointsDisplay
                            points={parseFloat(order.platinumPointsTotal)}
                          />
                        </Text>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                </Box>
              )
              : null
          }
          {
            order.allAdjustments
              .filter(({ eligible, sourceType }) => eligible && (sourceType === 'Spree::PromotionAction' || sourceType === 'Spree::StockLocation'))
              .map(({
                id,
                label,
                amount,
                promotionCode,
              }) => (
                <Box paddingBottom={0.5} key={id}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Level.Item>
                        <Text hasEllipsis>
                          {(promotionCode && promotionCode.value)
                            ? `Promotion (${promotionCode.value})`
                            : label}
                        </Text>
                      </Level.Item>
                    </Level.Left>
                    <Level.Right>
                      <Level.Item>
                        <Text hasEllipsis><Price value={amount} /></Text>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                  {promotionCode && promotionCode.promotion && (
                    <Disclosure>
                      <Disclosure.Header>See details</Disclosure.Header>
                      <Disclosure.Content>
                        <Text hasEllipsis size="small" isBold>
                          {promotionCode.promotion.name}
                        </Text>
                        <Text display="block" size="tiny">{promotionCode.promotion.description}</Text>
                      </Disclosure.Content>
                    </Disclosure>
                  )}
                </Box>
              ))
          }

          <Box paddingBottom={0.5}>
            <Level>
              <Level.Left isShrinkable>
                <Level.Item>
                  <Text hasEllipsis>
                    {
                      order.state === 'confirm'
                        ? 'Taxes & Fees'
                        : 'Estimated Taxes & Fees'
                    }
                  </Text>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Text hasEllipsis><Price value={order.displayTaxTotal} /></Text>
                </Level.Item>
              </Level.Right>
            </Level>
          </Box>
          {
            appliedGiftCardBalance > 0
              ? (
                <Box paddingBottom={0.5}>
                  <Level>
                    <Level.Left isShrinkable>
                      <Level.Item>
                        <Text hasEllipsis>Applied Gift Cards</Text>
                      </Level.Item>
                    </Level.Left>
                    <Level.Right>
                      <Level.Item>
                        <Text hasEllipsis> <Price value={-appliedGiftCardBalance} /></Text>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                </Box>
              )
              : null
          }

          <Divider size="small" />

          <Sticky background="main">
            <Level>
              <Level.Left isShrinkable>
                <Level.Item>
                  <Clipped>
                    <Text hasEllipsis size="large">
                      {
                        order.state === 'confirm'
                          ? 'Total'
                          : 'Estimated Total'
                      }
                    </Text>
                  </Clipped>
                </Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>
                  <Clipped>
                    <Text hasEllipsis size="large" isBold><Price value={orderTotal} /></Text>
                  </Clipped>
                </Level.Item>
              </Level.Right>
            </Level>
          </Sticky>

          <Box paddingVertical={2}>
            {order.lineItems.map(lineItem => (
              <Media key={lineItem.id}>
                {!!(lineItem.variant && lineItem.variant.images.length) && (
                  <Media.Figure size="small">
                    <Image
                      src={lineItem.variant.images[0].smallUrl}
                      alt={lineItem.variant.name}
                      maxHeight={4}
                    />
                  </Media.Figure>
                )}
                <Media.Content>
                  <Level>
                    <Level.Item>
                      <Clipped>
                        <Text hasEllipsis>{lineItem.variant.name}</Text>
                        <Text size="tiny" hasEllipsis>
                          {lineItem.variant.optionValues.map(({ presentation }) => presentation).join(' ')}
                        </Text>
                      </Clipped>
                    </Level.Item>
                    <Level.Right>
                      <Level.Item>
                        <Clipped>
                          <Text display="block"><Price value={lineItem.price} /></Text>
                          {!!(lineItem.quantity > 1) && (
                            <Text size="tiny" hasEllipsis alignment="right">Qty. {lineItem.quantity}</Text>
                          )}
                        </Clipped>
                      </Level.Item>
                    </Level.Right>
                  </Level>

                </Media.Content>
              </Media>
            ))}
          </Box>
        </Drawer.Content>
        {!!(isSubmitting || (!isLoading && order.state === 'confirm')) && (
          <Box padding={0.5}>
            <Button
              type="button"
              icon="lock"
              color="action"
              isFullwidth
              isLoading={isSubmitting}
              onClick={submitOrder}
            >
              Place Order
            </Button>
          </Box>
        )}
      </Drawer>
    </Sticky>
  )
}

MiniOrderSummary.propTypes = {
  order: PropTypes.shape({
    number: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }), // private
  step: PropTypes.string, // private
  submitOrder: PropTypes.func, // private
  isLoading: PropTypes.bool, // private
  isSubmitting: PropTypes.bool, // private
}

MiniOrderSummary.defaultProps = {
  order: {},
  submitOrder: () => {},
  isLoading: false,
  isSubmitting: false,
}

export default withCheckout(MiniOrderSummary)
